import UploadFileInterface from '../../types/upload-file';
import * as FilePond from 'filepond';
import {FilePondFile} from 'filepond';
import LanguageUtility from '../utilities/language-utility';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';

/**
 * Represents a single uploaded file.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
class UploadFile implements UploadFileInterface {
    uuid: string;
    fileName: string;
    uploaded: boolean;

    /**
     * Constructor
     *
     * @param {any} data Data to initialize file with
     * @param {any} data.uuid UUID of the file
     * @param {any} data.fileName File name
     * @param {any} [data.uploaded] If true, then the file has already been uploaded; only used to initialize FilePond correctly
     */
    constructor(data: any) {
        this.uuid = data.uuid.trim();
        this.fileName = data.fileName.trim();
        this.uploaded = data.hasOwnProperty('uploaded') ? data.uploaded : false;
    }
}

/**
 * Implements file upload via a hidden field and the FilePond library.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class FileUpload {
    /**
     * File upload field.
     */
    private readonly fileField: HTMLInputElement;

    /**
     * Hidden field that will be updated with uploaded files.
     */
    private hiddenField: HTMLInputElement;

    /**
     * Submit button of the form.
     */
    private submitButton: HTMLButtonElement;

    /**
     * Container for displaying a warning message. May be hidden or shown on demand.
     */
    private warningContainer: HTMLElement;

    /**
     * Container for displaying an error message. May be hidden or shown on demand.
     */
    private errorContainer: HTMLElement;

    /**
     * FilePond instance.
     */
    private readonly pondInstance: FilePond.FilePond;

    constructor(fileField: HTMLInputElement, hiddenField: HTMLInputElement) {
        this.fileField = fileField;
        this.hiddenField = hiddenField;
        const form = this.fileField.closest<HTMLFormElement>('form');
        if (!form) {
            throw new Error('Required elements missing');
        }
        const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
        const warningContainer = document.querySelector<HTMLElement>('#fileUploadWarning');
        const errorContainer = document.querySelector<HTMLElement>('#fileUploadError');
        if (!submitButton || !warningContainer || !errorContainer) {
            throw new Error('Required elements missing');
        }

        this.submitButton = submitButton;
        this.warningContainer = warningContainer;
        this.errorContainer = errorContainer;

        // Get initial files
        const initialFiles = this.getInitialFiles();
        const initialFilePondFiles = this.getInitialFilesForFilePond(initialFiles);

        // Register additional FilePond plugins
        FilePond.registerPlugin(FilePondPluginImagePreview);
        FilePond.registerPlugin(FilePondPluginFileValidateSize);

        // Initialize FilePond
        this.pondInstance = FilePond.create(this.fileField, {
            server: '/upload/',
            credits: false,
            files: initialFilePondFiles,
            allowReorder: true,
            labelIdle: LanguageUtility.getLabel('filePond.labelIdle'),
            labelFileProcessing: LanguageUtility.getLabel('filePond.labelFileProcessing'),
            labelFileProcessingError: LanguageUtility.getLabel('filePond.labelFileProcessingError'),

            // File size validation
            allowFileSizeValidation: true,
            maxFileSize: '4MB', // See Frontend.xlf labels where this size is also present
            labelMaxFileSize: LanguageUtility.getLabel('filePond.labelMaxFileSize'),
            labelMaxFileSizeExceeded: LanguageUtility.getLabel('filePond.labelMaxFileSizeExceeded'),

            onaddfilestart: () => {
                this.disableSubmitButton();
            },
            onaddfile: () => {
                this.enableSubmitButtonIfUploadsCompleted();
            },
            onupdatefiles: (files: FilePondFile[]) => {
                this.updateHiddenField(files);
                this.enableSubmitButtonIfUploadsCompleted();
            },
            onerror: (response) => {
                // Invalid file type
                if (response.code === 415) {
                    this.showError(LanguageUtility.getLabel('filePond.fileTypeError'));
                }
                // File too large
                else if (response.code === 416) {
                    this.showError(LanguageUtility.getLabel('filePond.fileSizeError'));
                }
                // General error
                else {
                    this.showError(LanguageUtility.getLabel('filePond.generalError'));
                }

                this.enableSubmitButtonIfUploadsCompleted();
            },
            onprocessfile: () => {
                const files: FilePondFile[] = this.pondInstance.getFiles();
                this.updateHiddenField(files);
                this.enableSubmitButtonIfUploadsCompleted();
            },
            onreorderfiles: (files: FilePondFile[]) => {
                this.updateHiddenField(files);
            }
        });
    }

    /**
     * Disables the form submit button.
     */
    private disableSubmitButton(): void {
        this.submitButton.disabled = true;
        this.showWarning(LanguageUtility.getLabel('filePond.submitDisabledWhileUploading'));
    }

    /**
     * Enables the form submit button.
     */
    private enableSubmitButton(): void {

        this.submitButton.disabled = false;
        this.hideWarning();
    }

    /**
     * Enables the form submit button, if all uploads are finished.
     */
    private enableSubmitButtonIfUploadsCompleted(): void {
        if (this.allUploadsFinished()) {
            this.submitButton.disabled = false;
            this.hideWarning();
        }
    }

    /**
     * Shows the warning container with the given message.
     *
     * @param {string} message Message to show
     */
    private showWarning(message: string): void {
        const messageElement = this.warningContainer.querySelector<HTMLElement>('.message');
        if (messageElement) {
            messageElement.textContent = message;
        }
        this.warningContainer.classList.remove('d-none');
    }

    /**
     * Hides the warning container and removes any current message within it.
     */
    private hideWarning(): void {
        const messageElement = this.warningContainer.querySelector<HTMLElement>('.message');
        if (messageElement) {
            messageElement.textContent = '';
        }
        this.warningContainer.classList.add('d-none');
    }

    /**
     * Shows the error container with the given message.
     *
     * @param {string} message Message to show
     */
    private showError(message: string): void {
        const messageElement = this.errorContainer.querySelector<HTMLElement>('.message');
        if (messageElement) {
            messageElement.textContent = message;
        }
        this.errorContainer.classList.remove('d-none');
    }

    /**
     * Hides the error container and removes any current message within it.
     */
    private hideError(): void {
        const messageElement = this.errorContainer.querySelector<HTMLElement>('.message');
        if (messageElement) {
            messageElement.textContent = '';
        }
        this.errorContainer.classList.add('d-none');
    }

    /**
     * Checks if all uploads are finished.
     */
    private allUploadsFinished(): boolean {
        const files: FilePondFile[] = this.pondInstance.getFiles();
        let finished = true;

        files.forEach((file) => {
            if (
                file.status !== FilePond.FileStatus.PROCESSING_COMPLETE &&
                file.status !== FilePond.FileStatus.IDLE &&
                file.status !== FilePond.FileStatus.LOAD_ERROR && // Ignore files that triggered an error on the client side
                file.status !== FilePond.FileStatus.PROCESSING_ERROR // Ignore files that triggered an error during processing on the server side
            ) {
                finished = false;
            }
        });

        return finished;
    }

    /**
     * Gets the FilePond instance.
     *
     * @returns {FilePond}
     */
    get pond(): FilePond.FilePond {
        return this.pondInstance;
    }

    /**
     * Gets initial files found in the hidden field at domready.
     *
     * @returns {UploadFile[]}
     */
    private getInitialFiles(): UploadFile[] {
        if (!this.hiddenField.value.trim().length) {
            return [];
        }

        const files: UploadFile[] = [];
        const fileData = JSON.parse(this.hiddenField.value.trim());
        fileData.forEach((data: any) => {
            const file = new UploadFile(data);
            files.push(file);
        });

        return files;
    }

    /**
     * Takes the list of initially found files, and prepares them for the FilePond constructor.
     *
     * @param {UploadFile[]} files Initial files
     * @returns {any[]}
     */
    private getInitialFilesForFilePond(files: UploadFile[]): any[] {
        const initialData: any[] = [];

        for (const file of files) {
            initialData.push({
                source: file.uuid,

                // Set type to 'local' to tell FilePond this is an uploaded file
                // Set type to 'limbo' to tell FilePond this is a temp file (currently unsupported)
                options: {
                    type: 'local'
                }
            });
        }

        return initialData;
    }

    /**
     * Updates the hidden field with a list of files created by FilePond.
     *
     * @param {FilePondFile[]} pondFiles FilePond files
     */
    private updateHiddenField(pondFiles: FilePondFile[]) {
        const fileInfo: any[] = [];

        pondFiles.forEach((pondFile: FilePondFile) => {
            if (pondFile.serverId) {
                fileInfo.push({
                    uuid: pondFile.serverId,
                    fileName: pondFile.filename
                    // Note that the uploaded flag is not used here. The PHP backend of the FilePond upload will take care of these checks.
                });
            }
        });

        this.hiddenField.setAttribute('value', JSON.stringify(fileInfo));
    }
}
